import { AddBoxOutlined, Search, Public } from '@mui/icons-material';
import { Autocomplete, Box, Button, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { BodyText, CustomChip, SubTitleText } from 'Application/components';
import { AgenciesTable } from 'Application/components/AgenciesTable';
import { useAgencyStore, useRegionStore } from 'Application/store';
import { getUserRegionFromStorage, getUserRoleNameFromStorage } from 'Application/utils';
import { FC, useEffect, useState } from 'react';
import Loader from 'ui-component/Loader';
import { UserRole, RegionResponse, RegionItem } from 'Application/models';

interface AgenciesProps {}

export const Agencies: FC<AgenciesProps> = () => {
    const { agencyData, getAgencies, isLoading } = useAgencyStore();
    const [isAddAgencyModalOpen, setIsAddAgencyModalOpen] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const { regions: countries, getRegions } = useRegionStore();

    // const userRegion = getUserRegionFromStorage();
    const [userRegionName, setUserRegionName] = useState<string | null>(null);
    const [region, setRegion] = useState<RegionItem>({ name: 'All', regionID: 1, flag: '', countryAbbreviation: '' });
    const initialCountryValues: RegionResponse = [
        { name: 'Search by country', regionID: 0, flag: '', countryAbbreviation: '' },
        { name: 'All', regionID: 1, flag: '', countryAbbreviation: '' }
    ];
    const countryValues = countries ? [...initialCountryValues, ...countries] : initialCountryValues;
    const userRole = getUserRoleNameFromStorage();

    const handleCountryChange = (region: RegionItem) => {
        setRegion(region);
        region.name === 'All' ? setUserRegionName(null) : setUserRegionName(region.name);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (searchInput === '') setSearchTerm('');
    }, [searchInput]);

    useEffect(() => {
        getRegions();
    }, []);

    useEffect(() => {
        // if (userRegionName) {
        getAgencies(userRegionName, currentPage, rowsPerPage, searchTerm);
        // }
    }, [userRegionName, currentPage, rowsPerPage, searchTerm]);

    if (isLoading) return <Loader />;

    return (
        <Box className="border rounded-xl shadow-sm border-slate-200">
            <Box className="flex flex-wrap justify-between items-center p-6">
                <Box className="flex flex-col space-y-2">
                    <Box className="flex items-end space-x-2">
                        <SubTitleText text="Agencies Assigned" />
                        <CustomChip text={`${agencyData?.content.length || 0} Agencies`} />
                    </Box>
                    {/* <BodyText text={`Region: ${userRegionName || 'Unknown'}`} /> */}
                    <BodyText text="Manage users assigned to this product" />
                </Box>
                <Box
                    className={`flex justify-end items-center gap-4 mt-4 sm:mt-0 w-full ${userRole === UserRole.ROLE_ADMINISTRATOR ? 'sm:w-3/4' : 'sm:w-2/3'}`}
                >
                    {userRole === UserRole.ROLE_ADMINISTRATOR && (
                        <Autocomplete
                            className="w-1/3"
                            size="small"
                            disableClearable
                            options={countryValues}
                            value={region}
                            onChange={(_, newValue) => newValue && handleCountryChange(newValue)}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                                <MenuItem
                                    disabled={option.name === 'Search by country'}
                                    {...props}
                                    key={option.regionID}
                                    value={option.name}
                                    className={`flex gap-${option.name === 'All' ? 0 : 2} ${option.name === 'All' && '-ml-[1px]'}`}
                                >
                                    {option.name !== 'Search by country' &&
                                        (option.name === 'All' ? (
                                            <InputAdornment position="start">
                                                <Public sx={{ color: '#0369a1' }} />
                                            </InputAdornment>
                                        ) : (
                                            <img
                                                src={`data:image/png;base64, ${option.flag}`}
                                                alt={`Flag of ${option.name}`}
                                                width={20}
                                                height={20}
                                            />
                                        ))}
                                    <Typography>{option.name}</Typography>
                                </MenuItem>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {region.name === 'All' ? (
                                                    <Public className="ml-2 -mr-2" sx={{ color: '#0369a1' }} />
                                                ) : (
                                                    <img
                                                        className="ml-2 -mr-2"
                                                        src={`data:image/png;base64, ${region?.flag}`}
                                                        alt={`Flag of ${region?.name}`}
                                                        width={20}
                                                        height={20}
                                                    />
                                                )}
                                            </InputAdornment>
                                        )
                                    }}
                                    placeholder="Search by country"
                                />
                            )}
                        />
                    )}
                    <TextField
                        className="w-1/3"
                        size="small"
                        label="Search by name or email"
                        variant="outlined"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && setSearchTerm(searchInput)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search className="hover:cursor-pointer" onClick={() => setSearchTerm(searchInput)} />
                                </InputAdornment>
                            )
                        }}
                    />
                    {/* <Button className="text-sky-700 border-sky-200 bg-sky-50 rounded-md" variant="outlined" startIcon={<AddBoxOutlined />}>Assign Agency</Button> */}
                </Box>
            </Box>
            <AgenciesTable data={agencyData?.content || []} hasCheckboxSelection />
        </Box>
    );
};
